import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { settingsKeys } from '../hooks/useSettings';
import { settingsService, type PublicTenantSettings, type TenantSettings } from '../services/api/settings.service';
import { useAuthStore } from '../stores/auth.store';
import { resolveTheme, useUiStore } from '../stores/ui.store';

type BrandingSettings = TenantSettings | PublicTenantSettings;

/* ─── Types ─── */
export interface BrandingContext {
  settings: BrandingSettings | null;
  isLoading: boolean;
  /** Current theme mode actually applied */
  themeMode: 'light' | 'dark';
  /** Toggle between light / dark */
  toggleTheme: () => void;
  /** Force a specific mode */
  setThemeMode: (mode: 'light' | 'dark') => void;
  /** Refresh settings from API */
  refresh: () => void;
}

const ThemeCtx = createContext<BrandingContext>({
  settings: null,
  isLoading: true,
  themeMode: 'light',
  toggleTheme: () => {},
  setThemeMode: () => {},
  refresh: () => {},
});

export const useBranding = () => useContext(ThemeCtx);

/* ─── Helpers ─── */

/** Generate a simple shade palette from a single hex  */
function generateShades(hex: string): Record<string, string> {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  const lighten = (amt: number) => {
    const lr = Math.min(255, Math.round(r + (255 - r) * amt));
    const lg = Math.min(255, Math.round(g + (255 - g) * amt));
    const lb = Math.min(255, Math.round(b + (255 - b) * amt));
    return `${lr} ${lg} ${lb}`;
  };

  const darken = (amt: number) => {
    const dr = Math.max(0, Math.round(r * (1 - amt)));
    const dg = Math.max(0, Math.round(g * (1 - amt)));
    const db = Math.max(0, Math.round(b * (1 - amt)));
    return `${dr} ${dg} ${db}`;
  };

  return {
    '50': lighten(0.92),
    '100': lighten(0.84),
    '200': lighten(0.7),
    '300': lighten(0.5),
    '400': lighten(0.25),
    '500': `${r} ${g} ${b}`,
    '600': darken(0.15),
    '700': darken(0.3),
    '800': darken(0.45),
    '900': darken(0.6),
    '950': darken(0.75),
  };
}

/** Load a Google Font dynamically */
function loadGoogleFont(family: string) {
  const id = `gf-${family.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
}

/* ─── Provider ─── */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStore();
  const publicWorkspaceHost = useMemo(() => {
    if (typeof window === 'undefined') return undefined;

    const hostname = window.location.hostname.trim().toLowerCase();
    if (!hostname || hostname === 'localhost' || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)) {
      return undefined;
    }

    return window.location.host || hostname;
  }, []);

  const { data: authenticatedSettings, isLoading: isAuthenticatedSettingsLoading } = useQuery<TenantSettings>({
    queryKey: settingsKeys.tenant(),
    queryFn: () => settingsService.getSettings(),
    staleTime: 2 * 60 * 1000,
    retry: 1,
    enabled: isAuthenticated, // Only fetch when authenticated
  });

  const { data: publicSettings, isLoading: isPublicSettingsLoading } = useQuery<PublicTenantSettings>({
    queryKey: settingsKeys.publicTenant(publicWorkspaceHost),
    queryFn: () => settingsService.getPublicSettings(publicWorkspaceHost),
    staleTime: 2 * 60 * 1000,
    retry: 1,
    enabled: !isAuthenticated && Boolean(publicWorkspaceHost),
  });

  const settings = isAuthenticated ? (authenticatedSettings ?? publicSettings ?? null) : (publicSettings ?? null);
  const isLoading = isAuthenticated ? isAuthenticatedSettingsLoading : isPublicSettingsLoading;

  /* ─ Theme mode management ─
     Preference (light/dark/system) lives in the persisted ui store; this
     provider resolves it against the OS and stamps <html> so Tailwind's
     class-based dark variants and the token layer both switch together. */
  const themePreference = useUiStore((s) => s.themeMode);
  const setThemePreference = useUiStore((s) => s.setThemeMode);
  const density = useUiStore((s) => s.density);
  const [themeMode, setAppliedTheme] = useState<'light' | 'dark'>(() => resolveTheme(themePreference));

  useEffect(() => {
    const apply = () => setAppliedTheme(resolveTheme(useUiStore.getState().themeMode));
    apply();
    if (themePreference !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [themePreference]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', themeMode === 'dark');
  }, [themeMode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  const setThemeMode = useCallback((mode: 'light' | 'dark') => setThemePreference(mode), [setThemePreference]);
  const toggleTheme = useCallback(() => {
    setThemePreference(resolveTheme(useUiStore.getState().themeMode) === 'dark' ? 'light' : 'dark');
  }, [setThemePreference]);

  const refresh = useCallback(() => {
    if (isAuthenticated) {
      queryClient.invalidateQueries({ queryKey: settingsKeys.tenant() });
    } else {
      queryClient.invalidateQueries({ queryKey: settingsKeys.publicTenant(publicWorkspaceHost) });
    }
  }, [isAuthenticated, publicWorkspaceHost, queryClient]);

  /* ─ Apply CSS variables whenever settings or theme change ─ */
  useEffect(() => {
    if (!settings) return;

    const root = document.documentElement;

    // Primary color → CSS variables for full palette
    if (settings.primaryColor) {
      const shades = generateShades(settings.primaryColor);
      Object.entries(shades).forEach(([shade, rgb]) => {
        root.style.setProperty(`--color-primary-${shade}`, rgb);
      });
      root.style.setProperty('--color-primary', shades['500']);
      // The phone's status bar and the installed app's title bar follow the workspace colour.
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', settings.primaryColor);
    }

    // Accent color
    if (settings.accentColor) {
      const shades = generateShades(settings.accentColor);
      Object.entries(shades).forEach(([shade, rgb]) => {
        root.style.setProperty(`--color-accent-${shade}`, rgb);
      });
    }

    // Sidebar/header colors — tenant brand overrides were configured against
    // the light theme, so they apply in light mode only; dark mode always uses
    // the dark surface defaults to avoid light-branded chrome on dark canvas.
    const isDark = themeMode === 'dark';
    const sidebarBgValue = (!isDark && settings.sidebarBg) || (isDark ? '#151E31' : '#ffffff');
    const sidebarTextValue = (!isDark && settings.sidebarText) || (isDark ? '#E8EDF7' : '#334155');
    root.style.setProperty('--sidebar-bg', sidebarBgValue);
    root.style.setProperty('--sidebar-text', sidebarTextValue);

    const headerBgValue = (!isDark && settings.headerBg) || (isDark ? '#151E31' : '#ffffff');
    const headerTextValue = (!isDark && settings.headerText) || (isDark ? '#E8EDF7' : '#0f172a');
    root.style.setProperty('--header-bg', headerBgValue);
    root.style.setProperty('--header-text', headerTextValue);

    // Typography
    if (settings.headingFont && settings.headingFont !== 'Inter') {
      loadGoogleFont(settings.headingFont);
      root.style.setProperty('--font-heading', `'${settings.headingFont}', system-ui, sans-serif`);
    } else {
      root.style.setProperty('--font-heading', "'Inter var', 'Inter', system-ui, sans-serif");
    }

    if (settings.bodyFont && settings.bodyFont !== 'Inter') {
      loadGoogleFont(settings.bodyFont);
      root.style.setProperty('--font-body', `'${settings.bodyFont}', system-ui, sans-serif`);
    } else {
      root.style.setProperty('--font-body', "'Inter var', 'Inter', system-ui, sans-serif");
    }

    root.style.setProperty('--font-size-base', `${settings.baseFontSize || 14}px`);
    root.style.setProperty('--font-weight-heading', `${settings.headingWeight || 700}`);

    // Border radius
    root.style.setProperty('--radius', `${settings.borderRadius ?? 8}px`);

    // Compact mode
    root.classList.toggle('compact', !!settings.compactMode);

    // Favicon: the tenant's icon replaces both default icons (.ico and .svg in
    // index.html); a browser that prefers the SVG would otherwise keep ours.
    const faviconUrl = settings.faviconUrl;
    if (faviconUrl) {
      document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']").forEach((link) => {
        link.href = faviconUrl;
        link.removeAttribute('type');
        link.removeAttribute('sizes');
      });
    }

    // Custom CSS (tenant branding feature — editable in Settings)
    const customStyleId = 'tenant-custom-css';
    let customStyle = document.getElementById(customStyleId) as HTMLStyleElement | null;
    if (settings.customCss) {
      if (!customStyle) {
        customStyle = document.createElement('style');
        customStyle.id = customStyleId;
        document.head.appendChild(customStyle);
      }
      customStyle.textContent = settings.customCss;
    } else if (customStyle) {
      customStyle.remove();
    }
  }, [settings, themeMode]);

  const ctx = useMemo<BrandingContext>(
    () => ({
      settings: settings ?? null,
      isLoading,
      themeMode,
      toggleTheme,
      setThemeMode,
      refresh,
    }),
    [settings, isLoading, themeMode, toggleTheme, setThemeMode, refresh],
  );

  return <ThemeCtx.Provider value={ctx}>{children}</ThemeCtx.Provider>;
}
